import { useMemo } from 'react';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import PlaceIcon from '@mui/icons-material/Place';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import GroupsIcon from '@mui/icons-material/Groups';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import PetsIcon from '@mui/icons-material/Pets';
import TempleBuddhistIcon from '@mui/icons-material/TempleBuddhist';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import type { WorldElementDto } from '../../api/queries/worlds';

interface WorldTimelineProps {
  elements: WorldElementDto[];
}

interface TimelineEntry {
  elementId: string;
  elementName: string;
  elementType: string;
  eventText: string;
}

const ELEMENT_TYPE_ICONS: Record<string, React.ReactNode> = {
  location: <PlaceIcon fontSize="small" />,
  magic_system: <AutoFixHighIcon fontSize="small" />,
  magic: <AutoFixHighIcon fontSize="small" />,
  culture: <GroupsIcon fontSize="small" />,
  society: <GroupsIcon fontSize="small" />,
  technology: <PrecisionManufacturingIcon fontSize="small" />,
  creature: <PetsIcon fontSize="small" />,
  deity: <TempleBuddhistIcon fontSize="small" />,
  religion: <TempleBuddhistIcon fontSize="small" />,
  mythology: <TempleBuddhistIcon fontSize="small" />,
  history: <HistoryEduIcon fontSize="small" />,
};

function getElementIcon(elementType: string): React.ReactNode {
  return ELEMENT_TYPE_ICONS[elementType] ?? <PublicIcon fontSize="small" />;
}

/**
 * Parse timeline entries from world elements that contain "## History" sections
 * in their descriptions. Each paragraph under the History heading becomes a
 * separate timeline entry.
 */
function parseTimelineEntries(elements: WorldElementDto[]): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  for (const el of elements) {
    if (!el.description) continue;

    // Look for ## History section in the description
    const historyMatch = el.description.match(
      /##\s*History\s*\n([\s\S]*?)(?=\n##\s|\n#\s|$)/i,
    );
    if (!historyMatch) continue;

    const historyContent = historyMatch[1].trim();
    if (!historyContent) continue;

    // Split into paragraphs / bullet points
    const lines = historyContent
      .split(/\n+/)
      .map((line) => line.replace(/^[-*]\s*/, '').trim())
      .filter((line) => line.length > 0);

    for (const line of lines) {
      entries.push({
        elementId: el.id,
        elementName: el.name,
        elementType: el.elementType,
        eventText: line,
      });
    }
  }

  return entries;
}

export function WorldTimeline({ elements }: WorldTimelineProps) {
  const entries = useMemo(() => parseTimelineEntries(elements), [elements]);

  if (entries.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          p: 4,
        }}
      >
        <Stack alignItems="center" spacing={1}>
          <HistoryEduIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="body1" color="text.secondary">
            Add history to your world elements to see the timeline
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Include a &quot;## History&quot; section in any element&apos;s
            description to populate events here.
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflow: 'hidden' }}>
      {/* Horizontal scrollable track */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 2,
          pt: 1,
          px: 1,
          // Timeline line across the middle
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: 2,
            bgcolor: 'divider',
            zIndex: 0,
          },
        }}
      >
        {entries.map((entry, idx) => (
          <Card
            key={`${entry.elementId}-${idx}`}
            sx={{
              minWidth: 250,
              maxWidth: 300,
              flexShrink: 0,
              position: 'relative',
              zIndex: 1,
            }}
            variant="outlined"
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                {getElementIcon(entry.elementType)}
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="primary"
                  noWrap
                >
                  {entry.elementName}
                </Typography>
                <Chip
                  size="small"
                  label={entry.elementType.replace(/_/g, ' ')}
                  variant="outlined"
                  sx={{ fontSize: 10, height: 18, ml: 'auto' }}
                />
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {entry.eventText}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
